import { useCallback } from 'react';

import { getDashboardRoutes } from '@/constants/routes';
import { getLegacyCMSUrlWithTarget } from '@/utils/helpers';

import useIsIframe from './useIsIframe';

type ContentType = 'course' | 'gallery';

/**
 * Hook that provides URL resolution based on iframe context.
 * When in iframe: returns legacy CMS URLs.
 * When standalone: returns Next.js dashboard routes.
 */
export default function useContentUrl() {
  const isIframe = useIsIframe();

  const getCourseUrl = useCallback((courseId: string, spaceId: string): string => {
    if (isIframe === true) {
      return getLegacyCMSUrlWithTarget(`#/coaches/${spaceId}/courses/${courseId}`);
    }
    return getDashboardRoutes(spaceId).course.byId(courseId);
  }, [isIframe]);

  const getGalleryUrl = useCallback((spaceId: string): string => {
    if (isIframe === true) {
      return getLegacyCMSUrlWithTarget(`#/coaches/${spaceId}/imageGallery`);
    }
    return getDashboardRoutes(spaceId).gallery;
  }, [isIframe]);

  const getContentUrl = useCallback((type: ContentType, id: string, spaceId: string): string => {
    switch (type) {
      case 'course':
        return getCourseUrl(id, spaceId);
      case 'gallery':
        return getGalleryUrl(spaceId);
      default:
        return getCourseUrl(id, spaceId);
    }
  }, [getCourseUrl, getGalleryUrl]);

  return {
    isIframe,
    getCourseUrl,
    getGalleryUrl,
    getContentUrl,
  };
}
